import { defer, take } from '../vfx-budget';
import { spawnSprite } from '../vfx-pool';
import { texCel, texCelArc, texLines } from '../vfx-tex2';
import { rnd } from './util';
/* ============================================================
   風格 B：賽璐珞拍（二維影格）
   ============================================================

   ## 一句話

   命中不是一團粒子，是**三張畫依序播完**。

   ## 為什麼是這個

   格鬥遊戲與動畫的打擊感從來不是靠亮度，是靠**幀**：命中的那一格畫面上
   出現一個誇張的形狀，下一格換一個更大的，第三格散掉。三格總長不到
   0.15 秒，但那三格是「有沒有打到」最強的訊號。

   而粒子系統的預設作法（生一堆點、變大、變淡）永遠做不出這件事 ——
   它是連續的，而連續就是軟。所以這個風格用 `defer()` 自己排一條時間軸。

   ## 三條規則

   1. **每一張都有黑描邊。** 同一張形狀畫兩次：先畫大 12% 的墨色，再畫本體。
      描邊是二維動畫可讀性的全部來源 —— 沒有它，淺色的爆炸打在淺色敵人上
      就消失了。
   2. **尺寸不變，換形狀。** 三張剪影的輪廓都不一樣（`texCel(0/1/2)`），
      靠換形狀推進，不靠放大。放大是粒子的語言。
   3. **色階只有三段，中間沒有漸層。** 白 → 暖橘 → 墨。

   ## 記憶點

   **暴擊的集中線**。第三格打出一圈放射直線 —— 那是漫畫語法，
   出現的瞬間讀者就知道「這下很重」，不需要任何亮度。

   ## 已知風險（使用者已知會）

   俯視鏡頭下二維剪影可能讀成「貼在畫面上的紙片」。壓住它的作法是
   壽命極短（三格共 0.16 秒）＋ 尺寸克制 —— 它必須是一閃而過的一拍，
   停留久了就會被看穿是平面。 */

export var CEL: any = {
  id: 'cel',
  name: 'B · 賽璐珞拍',
  tag: '三張手繪剪影逐格播放，打擊感來自幀不是光',
  c: { hot: 0xfff4e0, acc: 0xff7a33, ink: 0x120e0c, chip: 0x8d8a84 },
  /* 平塗風格最怕泛光把色塊邊緣暈開。門檻拉高到 0.86：場景的火光窗燈
     還吃得到（強度還原成 1.15），但特效的平塗白不會過門檻。 */
  bloom: { strength: 1.15, radius: 0.55, threshold: 0.86 },
  /* 二維風格靠「停」不靠「震」：暴擊頓幀給到 0.06 秒（其他風格的兩倍），
     震幅反而壓小 —— 兩個都給滿會變成畫面在抖。 */
  punch: { shake: 0.016, critStop: 0.06, camPush: 0.18 },
  bullet: { c: 0xfff4e0, w: 0.2, len: 2.2, ink: 0x120e0c },
  muzzle: muzzle,
  impact: impact
};

/** 一格：同一張剪影畫兩次（墨色大一圈在後、本體在前）。

    **順序不能反。** 兩者都是 `depthWrite: false` 的透明物件、同一個
    renderOrder，實際繪製順序就是生成順序 —— 先生的先畫、後生的疊在上面。 */
function frame(tex: any, x: number, y: number, z: number, s: number, c: number, t: number) {
  var rot = rnd(0, 6.283);
  spawnSprite(tex, x, y, z, {
    normal: true, c: CEL.c.ink, s: s * 1.14, t: t, op: 0.92, pow: 1.1, ease: 1, rot: rot
  });
  spawnSprite(tex, x, y, z, {
    normal: true, c: c, s: s, t: t, op: 1, pow: 1.1, ease: 1, rot: rot
  });
}

function muzzle(x: number, y: number, z: number, ang: number, power: number) {
  var big = power > 0.7;
  if (!take(big ? 8 : 4)) return;
  var s = 1.6 + power * 3.4;
  // 第一格：白，貼在槍口
  frame(texCel(0), x, y, z, s, CEL.c.hot, 0.05);
  // 第二格：暖橘，往射向前移一點並放大 —— 移動比放大更能讀出方向
  defer(0.05, function () {
    if (!take(2)) return;
    frame(texCel(1), x + Math.sin(ang) * s * 0.45, y, z + Math.cos(ang) * s * 0.45,
      s * 1.15, CEL.c.acc, 0.055);
  });
  if (!big) return;
  defer(0.11, function () {
    if (!take(2)) return;
    frame(texCel(2), x + Math.sin(ang) * s * 0.9, y, z + Math.cos(ang) * s * 0.9,
      s * 1.3, CEL.c.acc, 0.06);
  });
}

function impact(x: number, y: number, z: number, ang: number, crit: boolean) {
  if (!take(crit ? 8 : 4)) return;
  var k = crit ? 1.9 : 1.1;
  /* 第一格：一道弧。正面命中的那一格，手繪畫的不是圓也不是星，是弧 ——
     它同時說「打到了」與「力往哪個方向去」。弧口朝子彈來向的反側。 */
  var rot = -ang + Math.PI / 2;
  spawnSprite(texCelArc(), x, y, z, {
    normal: true, c: CEL.c.ink, s: 2.4 * k * 1.12, t: 0.05, op: 0.92, pow: 1.1, rot: rot
  });
  spawnSprite(texCelArc(), x, y, z, {
    normal: true, c: CEL.c.hot, s: 2.4 * k, t: 0.05, op: 1, pow: 1.1, rot: rot
  });
  // 第二格：換成不規則剪影、換成暖橘。形狀變了才有「推進」
  defer(0.05, function () {
    if (!take(2)) return;
    frame(texCel(crit ? 2 : 1), x, y, z, 2.1 * k, CEL.c.acc, 0.06);
  });
  if (!crit) return;
  /* 第三格（只有暴擊）：集中線。**這個風格的記憶點。**
     它不發亮、不擴散，就是一圈放射直線出現 0.07 秒 —— 漫畫語法。 */
  defer(0.10, function () {
    if (!take(2)) return;
    spawnSprite(texLines(14), x, y, z, {
      normal: true, c: CEL.c.hot, s: 5.5, s1: 7.5, t: 0.07, op: 0.9, pow: 1.4, ease: 2,
      rot: rnd(0, 6.283)
    });
  });
}
